if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      static shippingProtectionQueue = Promise.resolve();

      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.initialCustomInput = this.form.querySelector('[data-initial-custom-input]');
        this.initialCustomInput?.addEventListener('input', () => this.initialCustomInput.setCustomValidity(''));
        this.nameCustomInput = this.form.querySelector('[data-name-custom-input]');
        this.nameCustomInput?.addEventListener('input', () => this.nameCustomInput.setCustomValidity(''));
        this.numberCustomInput = this.form.querySelector('[data-number-custom-input]');
        this.numberCustomInput?.addEventListener('input', () => this.numberCustomInput.setCustomValidity(''));
      }


      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.closest('product-info')?.hasAttribute('data-variant-pending')) return;

        const initialCustomInput = this.initialCustomInput;
        if (initialCustomInput) {
          const initialCustomValue = initialCustomInput.value.trim().normalize('NFC');
          if (!/^\p{L}$/u.test(initialCustomValue)) {
            initialCustomInput.setCustomValidity(initialCustomInput.dataset.errorMessage);
            initialCustomInput.reportValidity();
            initialCustomInput.focus();
            return;
          }
          const uppercaseInitial = initialCustomValue.toUpperCase();
          initialCustomInput.value = /^\p{L}$/u.test(uppercaseInitial) ? uppercaseInitial : initialCustomValue;
          initialCustomInput.setCustomValidity('');
        }

        const nameCustomInput = this.nameCustomInput;
        if (nameCustomInput) {
          if (!nameCustomInput.value.trim()) {
            nameCustomInput.setCustomValidity('Please enter the name to personalize this item.');
            nameCustomInput.reportValidity();
            nameCustomInput.focus();
            return;
          }
          nameCustomInput.setCustomValidity('');
        }

        const numberCustomInput = this.numberCustomInput;
        if (numberCustomInput) {
          const numberCustomValue = numberCustomInput.value.trim();
          if (!numberCustomValue) {
            numberCustomInput.setCustomValidity('Please enter the number to personalize this item.');
            numberCustomInput.reportValidity();
            numberCustomInput.focus();
            return;
          }
          if (!/^\d+$/.test(numberCustomValue)) {
            numberCustomInput.setCustomValidity('Please enter numbers only.');
            numberCustomInput.reportValidity();
            numberCustomInput.focus();
            return;
          }
          numberCustomInput.setCustomValidity('');
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const shippingProtectionVariantId = this.querySelector('[data-shipping-protection-variant-id]:checked')?.value;

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        if (!shippingProtectionVariantId) config.body = formData;

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        const addToCart = async () => {
          if (shippingProtectionVariantId) {
            await this.prepareShippingProtectionAdd(config, formData, variantId, quantity, shippingProtectionVariantId);
          }

          const response = await fetch(`${routes.cart_add_url}`, config);
          return response.json();
        };

        const cartAddRequest = shippingProtectionVariantId
          ? ProductForm.enqueueShippingProtectionRequest(addToCart)
          : addToCart();

        cartAddRequest
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(e.message || 'Unable to update the cart.');
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      static enqueueShippingProtectionRequest(request) {
        const queuedRequest = this.shippingProtectionQueue.then(request, request);
        this.shippingProtectionQueue = queuedRequest.catch(() => undefined);
        return queuedRequest;
      }

      async prepareShippingProtectionAdd(config, formData, variantId, quantity, shippingProtectionVariantId) {
        const cart = await this.fetchCartData();
        const protectionLines = cart.items.filter(
          (item) => item.variant_id.toString() === shippingProtectionVariantId.toString()
        );

        if (protectionLines.length) await this.normalizeShippingProtectionLines(protectionLines);

        const mainItem = this.createCartLineItem(formData, variantId, quantity);
        const cartSections = this.cart?.getSectionsToRender().map((section) => section.id);
        const requestBody = { items: [mainItem] };

        if (!protectionLines.length) {
          requestBody.items.push({ id: shippingProtectionVariantId, quantity: 1 });
        }

        if (cartSections?.length) requestBody.sections = cartSections.join(',');
        if (this.cart) requestBody.sections_url = window.location.pathname;

        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify(requestBody);
      }

      async fetchCartData() {
        const cart =
          typeof CartItems !== 'undefined'
            ? await CartItems.fetchCartData()
            : await fetch(`${routes.cart_url}.json`).then((response) => {
                if (!response.ok) throw new Error('Unable to read the cart.');
                return response.json();
              });

        if (!cart?.items) throw new Error('Unable to read the cart.');
        return cart;
      }

      async normalizeShippingProtectionLines(protectionLines) {
        const totalQuantity = protectionLines.reduce((total, item) => total + item.quantity, 0);
        if (totalQuantity === 1 && protectionLines.length === 1) return;

        const updates = protectionLines.reduce((quantities, item, index) => {
          quantities[item.key] = index === 0 ? 1 : 0;
          return quantities;
        }, {});
        const response = await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ updates }),
        });
        const state = await response.json();

        if (!response.ok || state.status || state.errors) {
          throw new Error(state.description || state.message || state.errors || 'Unable to update shipping protection.');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }


      createCartLineItem(formData, variantId, quantity) {
        const item = { id: variantId, quantity };
        const properties = {};

        formData.forEach((value, key) => {
          const propertyMatch = key.match(/^properties\[(.+)]$/);
          if (propertyMatch) properties[propertyMatch[1]] = value;
          if (key === 'selling_plan' && value) item.selling_plan = value;
        });

        if (Object.keys(properties).length) item.properties = properties;
        return item;
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
